import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { useAppSelector } from 'store';

import { setIsAutoFinishTriggered } from 'modules/auto-test';

import { setConfirmationModal } from '../store';

dayjs.extend(duration);

type TimeUnitType = 'minute' | 'hour';

interface IProps {
  startTime?: number;
  countdownAmount?: number;
  unit?: TimeUnitType;
  onFinish?: () => void;
}

export const useCountdownTimer = ({
  startTime,
  countdownAmount = 180,
  unit = 'minute',
  onFinish
}: IProps): { remaining: string; isFinished: boolean } => {
  const [remaining, setRemaining] = useState<string>(unit === 'hour' ? '0h 0m' : `${countdownAmount}:00`);
  const [isFinished, setIsFinished] = useState(false);
  const stop = useAppSelector(state => state.privateTest.isRunning);
  const olympicStop = useAppSelector(({ olympicTest }) => olympicTest.isRunning);
  const isTimerRunning = stop || olympicStop;

  const dispatch = useDispatch();

  useEffect(() => {
    if (!startTime || !isTimerRunning) return undefined;

    setIsFinished(false); // Reset on change
    const totalMinutes = unit === 'hour' ? countdownAmount * 60 : countdownAmount;
    const normalizedStart = startTime < 1e12 ? startTime * 1000 : startTime;
    const endTime = dayjs(normalizedStart).add(totalMinutes, 'minute');

    const updateRemainingTime = () => {
      const now = dayjs();
      const diffMs = endTime.diff(now);

      if (diffMs <= 0) {
        setRemaining(unit === 'hour' ? '0s 0d' : '00:00');
        setIsFinished(true);
        onFinish?.();
        dispatch(setConfirmationModal(true));
        dispatch(setIsAutoFinishTriggered(true));

        return false;
      }

      const dur = dayjs.duration(diffMs);

      if (unit === 'hour') {
        const hours = Math.floor(dur.asHours());
        const minutes = dur.minutes();

        setRemaining(`${hours}s ${minutes}d`);
      } else {
        const minutes = Math.floor(dur.asMinutes());
        const seconds = String(dur.seconds()).padStart(2, '0');

        setRemaining(`${minutes}:${seconds}`);
      }

      return true;
    };

    updateRemainingTime();

    const interval = setInterval(() => {
      const shouldContinue = updateRemainingTime();

      if (!shouldContinue) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, countdownAmount, unit, stop, olympicStop, isTimerRunning]);

  return { remaining, isFinished };
};
